import { IconAlertTriangleFilled } from '@tabler/icons-react';
import { Ribbon } from 'icons';
import { useTranslation } from 'react-i18next';

import { mapCategoryToAliasTranslation } from '@/utils/categoryUtils';
import { getDueDateColor, getDueDateString } from '@/utils/dueDate';
import { getProgressBarText } from '@/utils/generateProgressBarText';
import { generateProgressText } from '@/utils/generateProgressText';
import { itemByLanguage } from '@/utils/language';
import { ComplianceUserPlanStatus } from '@/utils/migration';
import { getTimeSpentOnPlan } from '@/utils/time';

import Constants from '@/constants';

import PlanCard from '../PlanCard';
import PlanCardIcon from '../PlanCardComponents/PlanCardIcon';
import PlanCardLabel from '../PlanCardComponents/PlanCardLabel';
import PlanCardSubHeader from '../PlanCardComponents/PlanCardSubHeader';
import PlanCardTag from '../PlanCardComponents/PlanCardTag';
import PlanCardTitle from '../PlanCardComponents/PlanCardTitle';
import PlanCardContent from '../PlanCardContent/PlanCardContent';

interface ReportsTeamMemberCompliancePlanCardProps {
  userPlanStatus: ComplianceUserPlanStatus;
}

const ReportsTeamMemberCompliancePlanCard: React.FC<
  ReportsTeamMemberCompliancePlanCardProps
> = ({ userPlanStatus }) => {
  const { t } = useTranslation();
  const timeSpentOnPlan = getTimeSpentOnPlan({ ...userPlanStatus });
  const isComplete = userPlanStatus.planStatus === 'COMPLETED';
  const isFailed = userPlanStatus.isFailed ?? false;
  const isCompliance = true;
  const percentComplete = userPlanStatus.percentComplete ?? 0;
  const finalScore = userPlanStatus.finalScore;
  const isFoodSafety = itemByLanguage(userPlanStatus.planName)?.includes(
    Constants.FOOD_SAFETY_COURSE_NAME,
  );

  return (
    <>
      <PlanCard
        category={Constants.PLAN_CATEGORIES.COMPLIANCE}
        dataTestId={'TeamMemberCompliancePlan'}
        progress={!isComplete && !isFailed ? percentComplete : null}
        tooltip={t('Reports.compliancePlanTooltip')}
      >
        <PlanCardContent>
          <PlanCardLabel>
            {t(
              mapCategoryToAliasTranslation(
                Constants.PLAN_CATEGORIES.COMPLIANCE,
              ),
            )}
          </PlanCardLabel>
          <PlanCardTitle>
            {itemByLanguage(userPlanStatus.planName)}
          </PlanCardTitle>
          {isComplete && (
            <>
              <PlanCardSubHeader
                className="success"
                data-testid="PlanTaskCount"
              >
                {generateProgressText({
                  finalScore: finalScore?.toString(),
                  isCompliance,
                  isComplete,
                  isFailed,
                  isFoodSafety,
                  timeSpentOnPlan,
                })}
              </PlanCardSubHeader>
              <PlanCardSubHeader className="bold">
                {userPlanStatus.certificateExpiration ? (
                  t('TrainingPlans.certificationExpires', {
                    expirationDate: new Date(
                      userPlanStatus.certificateExpiration,
                    ).toLocaleDateString(),
                  })
                ) : (
                  <>
                    <IconAlertTriangleFilled className="text-feedback-warning-base! size-4!" />
                    <span className="ml-1">
                      {t('TrainingPlans.noExpirationDate')}
                    </span>
                  </>
                )}
              </PlanCardSubHeader>
            </>
          )}
          {isFailed && (
            <PlanCardSubHeader className="bold" data-testid="PlanTaskCount">
              {generateProgressText({
                finalScore: finalScore?.toString(),
                isCompliance,
                isComplete,
                isFailed,
                isFoodSafety,
                timeSpentOnPlan,
              })}
            </PlanCardSubHeader>
          )}
          {!isComplete && !isFailed && (
            <PlanCardSubHeader data-testid="PlanTaskCount">
              {getProgressBarText({
                isCompliance,
                isComplete,
                percentComplete,
                timeSpentOnPlan,
              })}
            </PlanCardSubHeader>
          )}
        </PlanCardContent>
        {!isComplete && !!userPlanStatus.dueDate && (
          <PlanCardTag
            color={getDueDateColor(userPlanStatus.dueDate)}
            text={getDueDateString(userPlanStatus.dueDate)}
            tooltip={t('TrainingPlans.accountability.due.dueDate', {
              dueDate: new Date(userPlanStatus.dueDate).toLocaleDateString(),
            })}
          />
        )}
        {isComplete && (
          <PlanCardIcon
            icon={<Ribbon className="size-7!" />}
            tooltip={
              <>
                <strong>{t('TrainingPlans.statusOptions.completed')}:</strong>
                {userPlanStatus.completionDate
                  ? new Date(userPlanStatus.completionDate).toLocaleDateString()
                  : t('Generic.na')}
              </>
            }
          />
        )}
      </PlanCard>
    </>
  );
};

export default ReportsTeamMemberCompliancePlanCard;
